// Mirror of functions/src/canvas/prompt.ts — duplicated per CLAUDE.md.
// The FE builds the system prompt itself and sends it as `system` to /chat
// so we don't need an extra round-trip to fetch the prompt.
//
// IMPORTANT: this file is the RUNTIME scoring prompt. It must stay in parity
// with functions/src/canvas/prompt.ts — a drift here (stale rubric / an old
// score cap) silently changes every grade. The prompt-parity test guards it.

import type { BudgetMode, CanvasSession, Complexity, DeploymentStage, FieldDef, PhaseCode } from "./schema";
import { findField, findPhase, PHASE_ORDER } from "./schema";
import spec from "./canvas-spec.json";

/**
 * The 1–5 scoring scale, rendered from the skill's quality-rubric.md (carried in
 * canvas-spec.json, so it is versioned with the skill and cannot silently drift).
 *
 * This used to be a five-line paraphrase. With no calibration anchors — no four
 * operationalisation elements, no "2 of 4" / "most or all" thresholds — the model
 * filled the vacuum with a stricter bar of its own (mandating field templates,
 * "[metric] [comparison] [value]" syntax, per-route trigger tables, structured
 * sub-field schemas) and graded ~1.5 below the skill on identical answers.
 * Anchoring on the skill's real scale puts both scorers on the same ruler.
 */
function buildRubric(): string {
  const r = spec.qualityRubric as {
    elements: string[];
    scores: Array<{
      score: number;
      label: string;
      state: string;
      characteristics: string[];
    }>;
  };
  const out: string[] = [
    `RUBRIC (score 1–5) — the ODC quality rubric (skill v${spec.skillVersion}).`,
    "",
    "An answer is 'operational' to the degree it carries these four elements:",
    ...r.elements.map((e) => `  - ${e}`),
    "",
  ];
  for (const s of r.scores) {
    out.push(`${s.score} — ${s.label}: ${s.state}`);
    for (const c of s.characteristics) out.push(`     · ${c}`);
  }
  out.push(
    "",
    "Score against THIS scale and nothing else. Do NOT invent additional structural requirements: no mandated field templates, no required metric syntax, no per-route trigger tables, no contact-detail or SLA formats the scale does not ask for.",
    "",
    "How to band the score — COUNT how many of the four elements the answer carries, then map: 0–1 elements → 1–2; exactly 2 of 4 → 3; 3 OR 4 of 4 → 4; a 5 when the answer is complete and specific enough to run as operational procedure without modification — a cross-reference to another Canvas field or explicit failure-mode handling is the surest route to a 5 but is NOT required; a fully operational, unambiguous answer a team could execute as-is also earns a 5. A 4 does NOT require all four elements — three of the four IS 'most', and 'most or all four' is a 4. Do not drop an otherwise-operational answer to 3 merely because one element is thin, because a threshold could be tighter, or because you would have emphasised a different angle (e.g. business-outcome vs technical framing, or a different example). If the answer is implementable without a follow-up question, it is at least a 4 even if you can imagine a more rigorous phrasing.",
    "",
    "CRITICAL — serialization vs. scoring: the field context above may list 'canonical export fields' (e.g. `risk_id, skill_at_risk, …`) or a format such as \"[metric] must be [comparison] [value]\". Those are hints for populating the exported canvas JSON, NOT scoring criteria. NEVER deduct points solely because the user's prose did not reproduce those sub-field names, a template, a table layout, or a naming scheme. An answer in plain prose that conveys the same substance — named entities, quantified thresholds, time-bound commitments, conditional logic — scores exactly the same as one that mirrors the field's structure.",
    "",
    "CAPABILITY vs. OWNERSHIP: when a field asks what human judgment, capability, intent, or outcome the system depends on, it is describing a KIND of judgment, not an org chart. Do NOT down-band such an answer for lacking a named individual, backup owner, deputy, or clearance/credential — those belong only to fields that explicitly ask WHO owns, approves, or can pause something. A specific, well-distinguished set of capabilities is a 4–5 even with no names attached.",
    "",
    "CONFIRM vs. DEMONSTRATE: a field phrased as a confirmation or checklist (\"owner confirms …\", \"each X is defensible / reviewed / logged\") is fully satisfied when the answer attests that governance state with specificity. Do NOT deduct because the answer does not also reproduce the underlying artifact — a verbatim rationale paragraph, a filled-in template, a sample record. Requiring the artifact turns a field that asks the owner to ATTEST into one that asks them to PRODUCE; score the attestation the field actually requests.",
  );
  return out.join("\n");
}

const RUBRIC = buildRubric();

const STRUCTURED_TAIL = `
At the end of your reply, on its OWN line, emit EXACTLY this block (no markdown fences, no extra text after):
<<<JSON>>>{"score": <1-5>, "gapFlagged": <true|false>, "gapNote": "<one-line gap or empty string>", "confidence": "<low|medium|high>"}<<<END>>>
The JSON must be valid. Do not wrap it in code fences. Do not add commentary after the <<<END>>> marker.
`;

const PUSHBACK_RULES = `
PUSHBACK BUDGET: {{budget}} remaining for this session.
If you choose to push back (because the answer is vague, optimistic, or skips a known failure mode):
 - say so in the prose
 - DO NOT advance — still emit a score (will usually be 1–3)
 - keep it firm but short
Use the budget sparingly. If the answer is good (4 or 5), affirm and move on.
`;

const TEACHING_MODE_BLOCK = `
COACH MODE: Teaching (deployment stage = new_design)
This user is designing a system that has not yet shipped. Adopt a teaching tone: explain WHY each question matters before pushing on the answer. Offer 1–2 concrete examples per evaluation when the user's answer is vague. Be patient with hand-waving on operational details — they often don't exist yet. Still emit a score, but score generously on intent and clarity; reserve low scores for genuine vagueness, not for "we haven't built it yet."
`;

const STANDARD_MODE_BLOCK = `
COACH MODE: Standard (deployment stage = operating_system)
This user is auditing a system that is already live. Use balanced pushback: name gaps directly when answers are vague or optimistic, but acknowledge what they've built. Treat the conversation as a baseline governance audit — your job is to surface what's working AND what isn't. Score on specificity, named owners, honest gaps, and cited mechanisms.
`;

const INVESTIGATION_MODE_BLOCK = `
COACH MODE: Investigation (deployment stage = post_incident)
This user is conducting a forensic review after a failure. Adopt a direct, investigation-mode tone: skip reassurance and pleasantries. For each answer, probe causation — what failed, when, what was the trigger, who noticed and how long after. Treat every gap-flagged answer as a forensic lead worth one specific follow-up. Score on whether the answer enables a future incident retrospective to reconstruct what happened.
`;

function modeBlock(stage: DeploymentStage | undefined): string {
  switch (stage) {
    case "new_design":
      return TEACHING_MODE_BLOCK;
    case "operating_system":
      return STANDARD_MODE_BLOCK;
    case "post_incident":
      return INVESTIGATION_MODE_BLOCK;
    default:
      return "";
  }
}

// The skill's complexity adjustment for multi-agent is: "Include all layers +
// per-agent governance in S1 and S4; require agent inventory." So asking for the
// inventory is aligned — but the previous wording ALSO hard-capped the score
// ("Score 4 or 5 only if…"), a gate the skill does not impose. A missing
// inventory already costs points under the rubric (absent Named Entities), so
// the extra cap only double-penalised. Ask for it; let the rubric score it.
const COMPLEXITY_BLOCK_MULTI_AGENT_TEAM = `
COMPLEXITY ADJUSTMENT: Multi-agent system
This system has multiple agents, so S1 and S4 need per-agent governance: the 'Human team' field should carry an agent inventory — name, role, and scope of action for each agent. If the user names humans but no agents, push back and ask for the agent list. Score the answer on the rubric as written; do not apply a separate score cap for this.
`;

// Solo operator: one person, one agent. Applied to every field so the scorer
// never marks an answer down for lacking a backup owner, deputy, second
// reviewer, or on-call rotation — for a solo operator a fail-safe hard-stop
// (nothing times out INTO action) IS the correct answer. This is what dropped
// DR2 ("who can pause unilaterally?") a full point in the TSLIT re-score.
const COMPLEXITY_BLOCK_SOLO_OPERATOR = `
COMPLEXITY ADJUSTMENT: Solo operator (one person, one agent)
There is no team. Do NOT deduct points for the absence of a backup owner, deputy, second reviewer, succession plan, or on-call rotation — naming a single owner is correct here. A fail-safe hard-stop (work halts / nothing proceeds without the owner, and nothing ever times out into action) is a fully valid answer for a solo operator. Score the substance against the rubric, not the size of the org.
`;

function complexityBlock(field: FieldDef, complexity: Complexity | undefined): string {
  if (complexity === "solo_operator") return COMPLEXITY_BLOCK_SOLO_OPERATOR;
  if (field.name === "human_team_foundation" && complexity === "multi_agent") {
    return COMPLEXITY_BLOCK_MULTI_AGENT_TEAM;
  }
  return "";
}

const PUSHBACK_PASS_1 = `
PUSHBACK PASS 1 — Gentle Probe
The user has answered once and the answer is vague (score < 4). Push back gently and curiously. Ask them to be more specific about: WHO is involved, WHAT signal/mechanism, WHAT cadence, and WHAT triggers action. Frame it as "Could you tell me more about…" — not "Your answer is bad." Wait for a fuller answer and re-evaluate.
`;

const PUSHBACK_PASS_2 = `
PUSHBACK PASS 2 — Example-Anchored Challenge
The user has revised once but the answer is still vague. Now reference what a strong answer looks like. Example template: "Alice monitors daily inference error rates. If error rate exceeds 5% for 2 consecutive days, she escalates to Bob for investigation." Tell the user their answer doesn't yet include: who, what signal, what cadence, what threshold. Ask them to provide those four specifically. Tone: assertive but not hostile — "This is what good governance looks like."
`;

const PUSHBACK_PASS_3 = `
PUSHBACK PASS 3 — Governance-Gap Warning
The user has revised twice and the answer is still vague. This is the final pushback. Flag the answer as a governance gap explicitly. Tell the user: "Without specific detection rules / named owners / cadence (whichever is missing), you risk blind spots — operators won't know when the agent drifts. This will appear in your deployment-readiness report as an open item." Offer a choice: revise now with one final attempt, OR accept as a flagged gap and move on. Tone: serious, non-punitive.
`;

// Pass 2 grounds "this is what good looks like" with the CURRENT field's own
// exemplar (passed in directly from field.exemplar). Each FieldDef carries its
// own exemplar from canvas-spec.json, so we never look it up by name — a
// name-keyed map would collide when a field name is reused across layers
// (e.g. quality_bar in both S2/S24 and S3/S33).
function pushbackBlock(pass: number | undefined, exemplar?: string, mode?: BudgetMode): string {
  if (mode === "critical_only") return "";
  if (mode === "reduced" && (pass === 2 || pass === 3)) {
    // Reduced mode caps escalation at Pass 1 even if the frontend counter is higher.
    return PUSHBACK_PASS_1;
  }
  if (pass === 1) return PUSHBACK_PASS_1;
  if (pass === 2) {
    if (exemplar) {
      return `
PUSHBACK PASS 2 — Example-Anchored Challenge
The user has revised once but the answer is still vague. Now reference a real example of what a strong answer for THIS field looks like.

Example (from Meridian Strategy Partners Canvas):
"${exemplar}"

Tell the user their answer doesn't yet match this level of specificity. Identify the missing dimensions (named owners, mechanisms, thresholds, cadence — whichever the exemplar shows that the user omitted). Ask them to revise with those dimensions. Tone: assertive but not hostile — "This is what good governance looks like."
`;
    }
    return PUSHBACK_PASS_2;
  }
  if (pass === 3) return PUSHBACK_PASS_3;
  return "";
}

export interface BuildPromptArgs {
  phase: PhaseCode;
  fieldIndex: number;
  session: CanvasSession;
  pushbackPass?: number;
}

export function buildSystemPrompt({ phase, fieldIndex, session, pushbackPass }: BuildPromptArgs): string {
  const phaseDef = findPhase(phase);
  const field = findField(phase, fieldIndex);

  const priorContext = summarisePriorAnswers(session, phase, fieldIndex);
  const mode = modeBlock(session.deploymentStage);
  const cAdjust = complexityBlock(field, session.complexity);
  const pBack = pushbackBlock(pushbackPass, field.exemplar, session.pushbackBudgetMode);

  return [
    `You are an Orchestration Coach evaluating a governance canvas answer.`,
    `Workflow: ${session.workflowName || "(unset)"}`,
    `Named owner: ${session.namedOwner || "(unset)"}`,
    ``,
    `ACTIVE PHASE: ${phaseDef.code} — ${phaseDef.title}`,
    `Phase subtitle: ${phaseDef.subtitle}`,
    ``,
    `ACTIVE FIELD: ${field.label} (${field.name})`,
    `Field intent: ${field.intent}`,
    // The exemplar is a real PROSE answer — the right thing to anchor "good" on.
    field.exemplar ? `Illustration of a strong answer (prose, NOT a required shape): "${field.exemplar}"` : "",
    // highQualityAnswerLooksLike carries the skill's canvas-JSON export schema
    // (sub-field names, "[metric] [comparison] [value]" formats). It is emitted
    // as a serialization hint ONLY — see the CRITICAL note in the rubric. Do not
    // reframe it as the answer bar; that is exactly what over-graded the web app.
    `Canonical export fields for ${field.code ?? field.name} (populate the exported canvas JSON — the user's prose does NOT need to name these sub-fields or match any template): ${field.highQualityAnswerLooksLike}`,
    ``,
    `Question being asked: "${field.question}"`,
    ``,
    priorContext ? `PRIOR ANSWERS SO FAR:\n${priorContext}` : "",
    ``,
    ...(mode ? [mode, ``] : []),
    ...(cAdjust ? [cAdjust, ``] : []),
    ...(pBack ? [pBack, ``] : []),
    `Your job — in 3 short paragraphs MAX:`,
    `1. Brief evaluation prose (2–4 sentences) telling the user where they nailed it and where they were vague.`,
    `2. If a specific gap exists, name it as "Gap:" with one concrete v2 commitment.`,
    `3. End with the structured JSON tail (see below).`,
    ``,
    RUBRIC,
    PUSHBACK_RULES.replace("{{budget}}", String(session.pushbackBudget)),
    STRUCTURED_TAIL,
  ]
    .filter(Boolean)
    .join("\n");
}

function summarisePriorAnswers(
  session: CanvasSession,
  currentPhase: PhaseCode,
  currentIdx: number
): string {
  const lines: string[] = [];
  for (const p of PHASE_ORDER) {
    const phase = findPhase(p);
    for (let i = 0; i < phase.fields.length; i++) {
      if (p === currentPhase && i >= currentIdx) break;
      const fld = phase.fields[i];
      const ans = session.phases[p].fields[fld.name];
      if (ans) {
        const compact = ans.answer.length > 240 ? ans.answer.slice(0, 240) + "…" : ans.answer;
        lines.push(`- ${p}.${fld.name} [score ${ans.score}]: ${compact}`);
      }
    }
    if (p === currentPhase) break;
  }
  return lines.join("\n");
}

export interface ParsedEval {
  score: number;
  gapFlagged: boolean;
  gapNote: string;
  confidence: "low" | "medium" | "high";
  proseText: string;
}

const TAIL_RE = /<<<JSON>>>([\s\S]*?)<<<END>>>/;

export function parseEvalTail(streamText: string): ParsedEval | null {
  const m = streamText.match(TAIL_RE);
  if (!m) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(m[1].trim());
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const p = parsed as Record<string, unknown>;
  const score = Number(p.score);
  if (!Number.isFinite(score) || score < 1 || score > 5) return null;
  const confidence = String(p.confidence ?? "medium");
  const conf: "low" | "medium" | "high" =
    confidence === "low" || confidence === "high" ? confidence : "medium";
  return {
    score: Math.round(score),
    gapFlagged: Boolean(p.gapFlagged),
    gapNote: String(p.gapNote ?? "").trim(),
    confidence: conf,
    proseText: streamText.slice(0, m.index).trim(),
  };
}

export function buildDRSystemPrompt(args: {
  drCode: string;
  drQuestion: string;
  session: CanvasSession;
  pushbackPass?: number;
}): string {
  const mode = modeBlock(args.session.deploymentStage);
  const pBack = pushbackBlock(args.pushbackPass, undefined, args.session.pushbackBudgetMode);
  // Deployment-readiness questions (esp. DR2 "who can pause unilaterally?") were
  // penalised for a solo operator not naming a backup. Carry the same complexity
  // adjustment the main canvas uses so a solo answer isn't marked down.
  const cAdjust =
    args.session.complexity === "solo_operator" ? COMPLEXITY_BLOCK_SOLO_OPERATOR : "";
  return [
    `You are an Orchestration Coach running the Deployment Readiness step.`,
    `Workflow: ${args.session.workflowName || "(unset)"}`,
    `Named owner: ${args.session.namedOwner || "(unset)"}`,
    ``,
    `ACTIVE STEP: ${args.drCode}`,
    `Question: "${args.drQuestion}"`,
    ``,
    ...(mode ? [mode, ``] : []),
    ...(cAdjust ? [cAdjust, ``] : []),
    ...(pBack ? [pBack, ``] : []),
    `Cross-reference earlier S1–S6 answers if relevant. Keep prose to 2–3 sentences.`,
    `Score 1–5 using the same rubric as the main canvas.`,
    RUBRIC,
    STRUCTURED_TAIL,
  ]
    .filter(Boolean)
    .join("\n");
}
